import { useAuthStore } from '@/stores/auth-store.js';
import { useWindowSize } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

export function useAppHelpers() {
    const { width } = useWindowSize();

    const breakPoint = computed(() => {
        if (width.value < 416) {
            return ['xxxs', 416, width.value]; // For widths less than 416
        } else if (width.value < 544) {
            return ['xxs', 544, width.value]; // For widths ≥416 and <544
        } else if (width.value < 640) {
            return ['xs', 640, width.value]; // For widths ≥544 and <640
        } else if (width.value < 768) {
            return ['sm', 768, width.value]; // For widths ≥640 and <768
        } else if (width.value < 1024) {
            return ['md', 1024, width.value]; // For widths ≥768 and <1024
        } else if (width.value < 1280) {
            return ['lg', 1280, width.value]; // For widths ≥1024 and <1280
        } else if (width.value < 1536) {
            return ['xl', 1536, width.value]; // For widths ≥1280 and <1536
        } else if (width.value < 1680) {
            return ['2xl', 1680, width.value]; // For widths ≥1536 and <1680
        } else if (width.value < 1920) {
            return ['3xl', 1920, width.value]; // For widths ≥1680 and <1920
        } else {
            return ['4xl', 1920, width.value]; // For widths ≥1920
        }
    });

    const currentBreakPoint = ref(breakPoint.value);
    watch(width, () => {
        currentBreakPoint.value = breakPoint.value;
        // You can add additional side effects here if needed.
    });

    const inLowerBreakPointRange = (breakPointName) => {
        const currentBP = breakPoint.value[0];

        const bpAliases = {
            xxxs: ['xxxs'],
            xxs: ['xxxs', 'xxs'],
            xs: ['xxxs', 'xxs', 'xs'],
            sm: ['xxxs', 'xxs', 'xs', 'sm'],
            md: ['xxxs', 'xxs', 'xs', 'sm', 'md'],
            lg: ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg'],
            xl: ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl'],
            '2xl': ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'],
            '3xl': ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'],
            '4xl': ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'],
        };

        return bpAliases[breakPointName] ? bpAliases[breakPointName].includes(currentBP) : currentBP === breakPointName;
    };

    const inHigherBreakPointRange = (breakPointName) => {
        const currentBP = breakPoint.value[0];

        const bpAliases = {
            '4xl': ['4xl'],
            '3xl': ['3xl', '4xl'],
            '2xl': ['2xl', '3xl', '4xl'],
            xl: ['xl', '2xl', '3xl', '4xl'],
            lg: ['xl', '2xl', '3xl', '4xl', 'lg'],
            md: ['xl', '2xl', '3xl', '4xl', 'lg', 'md'],
            sm: ['xl', '2xl', '3xl', '4xl', 'lg', 'md', 'sm'],
            xs: ['xl', '2xl', '3xl', '4xl', 'lg', 'md', 'sm', 'xs'],
            xxs: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'],
            xxxs: ['xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'],
        };

        return bpAliases[breakPointName] ? bpAliases[breakPointName].includes(currentBP) : currentBP === breakPointName;
    };
    const togglePassword = (element) => {
        if (element) {
            element.onclick = () => {
                var passwordInputs = document.querySelectorAll('.password-input');
                passwordInputs.forEach((elem) => {
                    if (elem.type === 'password') {
                        elem.type = 'text';
                        element.classList.remove('bx-show');
                        element.classList.add('bx-hide');
                    } else {
                        elem.type = 'password';
                        element.classList.remove('bx-hide');
                        element.classList.add('bx-show');
                    }
                });
            };
        }
    };

    const switchClasses = (element, addClass, removeClass) => {
        if (element) {
            element.classList.add(addClass);
            element.classList.remove(removeClass);
        }
    };

    const htmlDecode = (data) => {
        var txt = document.createElement('textarea');
        txt.innerHTML = data;
        return txt.value;
    };

    const htmlEncode = (input) => {
        const textArea = document.createElement('textarea');
        textArea.innerText = input;
        return textArea.innerHTML.split('<br>').join('\n');
    };

    const arraysAreEqual = (arr1, arr2) => {
        if (arr1 === undefined && arr2 === undefined) {
            return true;
        } else if (arr1 === undefined || arr2 === undefined) {
            return false;
        } else if (arr1.length !== arr2.length) {
            return false;
        }

        return arr1.every((value, index) => value === arr2[index]);
    };

    const deleteAllCookies = (cookieName = '', path = '') => {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

            if (cookie === '' || cookieName.trim() === name.trim()) {
            } else {
                continue;
            }
            deleteCookie(name, path);
        }
    };

    // A cookie can only be deleted by an expiry written with the same domain it was
    // set on. The server sets the session and XSRF cookies on SESSION_DOMAIN
    // (".inventree.test"), so a deletion without a domain attribute silently leaves
    // them in place and only clears a host-only cookie of the same name. That is how
    // a stale duplicate survives: the browser then sends both, PHP resolves the pair
    // to the first one, and the X-XSRF-TOKEN header stops matching the session it
    // loaded — a 419 on every request that a CSRF refresh cannot recover from.
    // So expire the host-only variant and every parent domain the host can set.
    const deleteCookie = (name, path = '') => {
        const key = name.trim();
        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        const paths = path === '' ? ['', '/'] : [path];

        const labels = window.location.hostname.split('.');
        const domains = [''];
        for (let i = 0; i < labels.length - 1; i++) {
            const domain = labels.slice(i).join('.');
            domains.push(domain, '.' + domain);
        }

        for (const cookiePath of paths) {
            for (const domain of domains) {
                let cookie = key + '=; ' + expiry;
                if (cookiePath !== '') cookie += '; path=' + cookiePath;
                if (domain !== '') cookie += '; domain=' + domain;
                document.cookie = cookie;
            }
        }
    };

    const getCookie = (name) => {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [key, value] = cookie.split('=');
            if (key.trim() === name) {
                return decodeURIComponent(value.trim());
            }
        }
        return '';
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const timeAgo = (date) => {
        date = date.trim();
        if (!date.endsWith('.000000Z')) {
            date += '.000000Z';
        }
        if (date.indexOf(' ') >= 0) {
            date = date.replace(/\s+/g, 'T');
        }

        const seconds = Math.floor((new Date().valueOf() - new Date(date).valueOf()) / 1000);

        let interval = Math.floor(seconds / 31536000);
        if (interval >= 1) {
            return interval + ' years ago';
        }

        interval = Math.floor(seconds / 2592000);
        if (interval >= 1) {
            return interval + ' months ago';
        }

        interval = Math.floor(seconds / 86400);
        if (interval >= 1) {
            return interval + ' days ago';
        }

        interval = Math.floor(seconds / 3600);
        if (interval >= 1) {
            return interval + ' hours ago';
        }

        interval = Math.floor(seconds / 60);
        if (interval >= 1) {
            return interval + ' minutes ago';
        }

        if (seconds < 10) return 'just now';

        return Math.floor(seconds) + ' seconds ago';
    };

    const getProfilePic = (link = '', full_name = 'Test User') => {
        if (!link) {
            let name = full_name
                .split(' ')
                .map((segment) => segment.trim().substring(0, 1))
                .join(' ')
                .trim();
            return 'https://ui-avatars.com/api/?name=' + encodeURIComponent(name) + '&color=7F9CF5&background=31445A';
        }
        return link;
    };

    /**
     * Copy to the clipboard, resolving to whether it actually worked so the
     * caller can confirm rather than assume. The Clipboard API needs a secure
     * context and permission; the textarea path covers the cases where it is
     * missing or refuses.
     */
    const copyText = async (text) => {
        const value = String(text ?? '');
        if (value === '') return false;

        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(value);
                return true;
            } catch {
                // Fall through to the selection-based copy.
            }
        }

        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = value;
        tempTextArea.setAttribute('readonly', '');
        tempTextArea.style.position = 'fixed';
        tempTextArea.style.opacity = '0';
        document.body.appendChild(tempTextArea);
        tempTextArea.select();
        tempTextArea.setSelectionRange(0, 99999);

        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        }

        document.body.removeChild(tempTextArea);

        return copied;
    };

    const statusFormatter = (val) => {
        return val.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
    };

    const formatDate = (val, type = 'date') => {
        if (!val) return '';

        const auth = useAuthStore();
        const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const userTimezone = auth?.user?.timezone ?? currentTimezone;

        // Bare Y-m-d must become a valid ISO datetime — appending ".000000Z"
        // alone produces "2025-06-04.000000Z" which is Invalid Date.
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
            val = `${val}T00:00:00.000000Z`;
        } else if (typeof val === 'string' && !val.endsWith('000Z')) {
            val = val.replace(' ', 'T') + '.000000Z';
        }

        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';

        const options = { timeZone: userTimezone };

        return type === 'date' ? date.toLocaleDateString('en-US', options) : date.toLocaleString('en-US', options);
    };

    const dateFormatter = (val) => formatDate(val, 'date');
    const dateTimeFormatter = (val) => formatDate(val, 'datetime');

    const toSingular = (word) => {
        const rules = [
            { plural: /ies$/, singular: 'y' },
            { plural: /es$/, singular: '' },
            { plural: /s$/, singular: '' },
        ];

        for (const rule of rules) {
            if (word.match(rule.plural)) {
                return word.replace(rule.plural, rule.singular);
            }
        }

        return word;
    };

    const loaderOn = (elem, toggleDisplay = false, lowerOpacity = true) => {
        if (elem) {
            elem.classList.add('loading');
            elem.style.opacity = '1';
            if (lowerOpacity) {
                elem.style.opacity = '0.6';
            }
            if (toggleDisplay) {
                elem.classList.remove('d-none');
            }
        }
    };

    const loaderOff = (elem, toggleDisplay = false) => {
        if (elem) {
            elem.classList.remove('loading');
            elem.style.removeProperty('opacity');
            if (toggleDisplay) {
                elem.classList.add('d-none');
            }
        }
    };

    const randomString = () => {
        const ts = new Date().getTime();
        return ts + '-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    };

    const mysqlDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const toInches = (input) => {
        // 1) Number → treat as feet
        if (typeof input === 'number') {
            return input * 12;
        }

        let str = String(input).trim().replace(/\s+/g, '');

        // Patterns for validation
        const feetInchesRe = /^(\d+(?:\.\d+)?)'(?:\s*(\d+(?:\.\d+)?)(?:")?)?$/;
        const inchesOnlyRe = /^(\d+(?:\.\d+)?)"$/;
        const bareFeetRe = /^\d+(?:\.\d+)?$/;

        let match;

        // 2) Feet + optional inches (e.g. 5' or 5'2" or 5' 2)
        if ((match = str.match(feetInchesRe))) {
            const feet = parseFloat(match[1]);
            const inches = parseFloat(match[2] || 0);
            return feet * 12 + inches;
        }

        // 3) Inches-only (e.g. 2")
        if ((match = str.match(inchesOnlyRe))) {
            return parseFloat(match[1]);
        }

        // 4) Bare number (e.g. 5 or 5.2) → treat as feet
        if (bareFeetRe.test(str)) {
            return parseFloat(str) * 12;
        }

        // 5) Anything else is invalid
        return 'Invalid measurement format';
    };

    const formatMeasurement = (input) => {
        let totalInches;
        try {
            totalInches = toInches(input);
        } catch (e) {
            return 'Invalid';
        }

        if (typeof totalInches !== 'number' || Number.isNaN(totalInches)) {
            return 'Invalid';
        }

        // Split into whole feet and the remainder in inches
        const feet = Math.floor(totalInches / 12);
        let inches = totalInches - feet * 12;

        // Round inches to at most 2 decimal places, trim trailing .0
        inches = Number(inches.toFixed(2));

        const parts = [];
        if (feet > 0) {
            parts.push(`${feet} ft`);
        }
        if (inches > 0) {
            parts.push(`${inches} in`);
        }

        // If both feet and inches are zero
        if (parts.length === 0) {
            return '0';
        }

        return parts.join(' ');
    };

    return {
        inLowerBreakPointRange,
        inHigherBreakPointRange,
        currentBreakPoint,
        breakPoint,
        togglePassword,
        switchClasses,
        htmlDecode,
        htmlEncode,
        deleteAllCookies,
        deleteCookie,
        getCookie,
        formatBytes,
        timeAgo,
        getProfilePic,
        copyText,
        statusFormatter,
        dateFormatter,
        dateTimeFormatter,
        toSingular,
        loaderOn,
        loaderOff,
        arraysAreEqual,
        randomString,
        mysqlDate,
        toInches,
        formatMeasurement,
    };
}
